import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { Button, Card, Callout, Spinner, CopyField, TextField, PasswordField, Modal, Badge, Checkbox, Select } from "@/design/components";
import { ApiError } from "@/api/client";
import {
  listChannels,
  createChannel,
  deleteChannel,
  rotateChannelSecret,
  setChannelRetention,
  isRelayUnavailable,
  type Channel,
  type CreateChannelInput,
  type RetentionInput,
} from "@/api/channels";
import { getUsage } from "@/api/admin";
import { listChannelAgents, type AgentSummary } from "@/api/agents";
import { AgentList } from "@/features/agents/AgentList";
import { TIER_LABEL } from "@/features/onboarding/tiers";
import { downloadCsv } from "@/lib/csv";
import styles from "./ChannelsScreen.module.css";

const apiMessage = (err: unknown, fallback: string): string =>
  err instanceof ApiError ? err.message : fallback;

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// --- Retention (Epic Channel-Retention) ---------------------------------------
// Idle windows offered in the UI. "1 month" = the relay default, sent as an ABSENT
// timeoutMs (stored null → inherit) so the default tracks the server, not a frozen
// constant. 1 week / 3 months send an explicit override.
const WEEK_MS = 604_800_000; // 7 days
const QUARTER_MS = 7_776_000_000; // 90 days
const RETENTION_OPTIONS = [
  { value: "week", label: "1 week" },
  { value: "month", label: "1 month (default)" },
  { value: "quarter", label: "3 months" },
];
/** Current stored timeout → the closest preset key (null / unknown ⇒ the 1-month default). */
function timeoutToPreset(ms: number | null): string {
  if (ms === WEEK_MS) return "week";
  if (ms === QUARTER_MS) return "quarter";
  return "month";
}
/** Preset key → the timeoutMs to send (undefined for the default = inherit/null). */
function presetToTimeout(preset: string): number | undefined {
  if (preset === "week") return WEEK_MS;
  if (preset === "quarter") return QUARTER_MS;
  return undefined;
}
/** Short human summary of a channel's retention for the list row. */
function retentionSummary(ch: Channel): string {
  if (ch.neverDelete) return "Permanent";
  if (ch.retentionTimeoutMs === WEEK_MS) return "Deletes after 1 week idle";
  if (ch.retentionTimeoutMs === QUARTER_MS) return "Deletes after 3 months idle";
  return "Deletes after 1 month idle";
}

/**
 * Channels (Story 3.2 / FR10) — the admin surface over the CP↔relay contract:
 * list the org's mapped Cogent channels, create one (which provisions a relay
 * session), and disconnect one. Each channel exposes the relay session id an
 * admin pastes into `/cogent map <id> <secret>` to bridge it to Slack.
 */
export function ChannelsScreen() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const channelsQuery = useQuery({
    queryKey: ["channels"],
    queryFn: ({ signal }) => listChannels(signal),
  });
  // Channel quota — same source the dashboard uses (shared cache key). We gate creation
  // on it so that at the limit we surface an explicit "upgrade" path instead of letting
  // the admin fill a form that only fails with a 429 on submit.
  const usageQuery = useQuery({ queryKey: ["usage"], queryFn: ({ signal }) => getUsage(signal) });
  // Live per-agent metadata across the org's channels (one call; grouped per channel
  // below by agent.sessionId === channel.relaySessionId). Degrades to [] server-side.
  const agentsQuery = useQuery({ queryKey: ["channel-agents"], queryFn: ({ signal }) => listChannelAgents(signal) });
  const agentsBySession = useMemo(() => {
    const map = new Map<string, AgentSummary[]>();
    for (const a of agentsQuery.data ?? []) {
      const list = map.get(a.sessionId);
      if (list) list.push(a);
      else map.set(a.sessionId, [a]);
    }
    return map;
  }, [agentsQuery.data]);

  const [createOpen, setCreateOpen] = useState(false);
  // After a successful create we keep the channel + the secret the admin just
  // typed so we can show the exact `/cogent map` command once — same one-time
  // reveal spirit as the Org_ID welcome screen.
  const [created, setCreated] = useState<{ channel: Channel; secret: string } | null>(null);
  const [toDelete, setToDelete] = useState<Channel | null>(null);

  const create = useMutation({
    mutationFn: (input: CreateChannelInput) => createChannel(input),
    onSuccess: (channel, input) => {
      void qc.invalidateQueries({ queryKey: ["channels"] });
      // Refresh usage too so the quota gate reflects the new count immediately (e.g.
      // creating the last allowed channel should gate the next create right away).
      void qc.invalidateQueries({ queryKey: ["usage"] });
      setCreated({ channel, secret: input.secret });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteChannel(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["channels"] });
      // Freeing a channel re-opens quota headroom → refresh so the gate lifts.
      void qc.invalidateQueries({ queryKey: ["usage"] });
      setToDelete(null);
    },
  });

  // Rotate a channel's secret. Like create, the new secret is shown ONCE (the relay
  // re-keys; the CP never stores it), so we keep the typed secret for the reveal.
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotateTarget, setRotateTarget] = useState<Channel | null>(null);
  const [rotated, setRotated] = useState<{ channel: Channel; secret: string } | null>(null);
  const rotate = useMutation({
    mutationFn: ({ channel, secret }: { channel: Channel; secret: string }) =>
      rotateChannelSecret(channel.id, secret),
    onSuccess: (_void, vars) => setRotated({ channel: vars.channel, secret: vars.secret }),
  });

  // Retention policy (never-delete + idle timeout). Optimistic: patch the cached
  // channel row on mutate so the list reflects the change instantly, roll back on
  // error, and reconcile with the server on settle.
  const [retentionTarget, setRetentionTarget] = useState<Channel | null>(null);
  const setRetention = useMutation({
    mutationFn: ({ id, input }: { id: string; input: RetentionInput }) => setChannelRetention(id, input),
    onMutate: async ({ id, input }) => {
      await qc.cancelQueries({ queryKey: ["channels"] });
      const prev = qc.getQueryData<Channel[]>(["channels"]);
      qc.setQueryData<Channel[]>(["channels"], (old) =>
        (old ?? []).map((c) =>
          c.id === id
            ? { ...c, neverDelete: input.neverDelete ?? false, retentionTimeoutMs: input.timeoutMs ?? null }
            : c,
        ),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      // Roll the optimistic patch back so a failed save never leaves a false state.
      if (ctx?.prev) qc.setQueryData(["channels"], ctx.prev);
    },
    onSuccess: () => setRetentionTarget(null),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["channels"] }),
  });
  function openRetention(ch: Channel) {
    setRetention.reset();
    setRetentionTarget(ch);
  }
  function closeRetention() {
    if (setRetention.isPending) return;
    setRetention.reset();
    setRetentionTarget(null);
  }

  function openCreate() {
    if (atChannelLimit) return; // at the limit → the upgrade CTA handles it, not the form
    create.reset();
    setCreated(null);
    setCreateOpen(true);
  }
  function closeCreate() {
    // Never dismiss an in-flight create: the success panel is the ONLY place the
    // one-time `/cogent map <id> <secret>` command is shown — closing mid-POST
    // would resolve into a hidden modal and lose the secret with no recovery.
    if (create.isPending) return;
    setCreateOpen(false);
  }
  function openRotate(ch: Channel) {
    rotate.reset();
    setRotated(null);
    setRotateTarget(ch);
    setRotateOpen(true);
  }
  function closeRotate() {
    // Like create, never dismiss mid-rotate: the success panel is the only place the
    // new secret + `/cogent map` command is shown.
    if (rotate.isPending) return;
    setRotateOpen(false);
    setRotateTarget(null);
  }
  function openDelete(ch: Channel) {
    // Clear any prior failure so a previous channel's "Couldn't disconnect" error
    // can't leak into this confirm modal (mirrors create.reset() in openCreate).
    remove.reset();
    setToDelete(ch);
  }
  function closeDelete() {
    if (remove.isPending) return;
    remove.reset();
    setToDelete(null);
  }

  const { data: channels, isPending, isError, error } = channelsQuery;
  const unavailable = isError && isRelayUnavailable(error);
  // At the plan's channel limit? (usage.channels.used >= limit). Undefined while usage
  // loads → treat as not-at-limit so a transient load never blocks a legitimate create;
  // the API still enforces the cap on submit as the backstop.
  const usage = usageQuery.data;
  const atChannelLimit = usage !== undefined && usage.channels.used >= usage.channels.limit;
  const canCreate = !isPending && !isError && !atChannelLimit;

  function exportCsv(): void {
    downloadCsv(
      `cogent-channels-${new Date().toISOString().slice(0, 10)}.csv`,
      ["Name", "Slack channels", "Live agents", "Created", "Relay session ID"],
      (channels ?? []).map((c) => [
        c.name,
        String(c.slackChannels),
        c.liveAgents === null ? "unknown" : String(c.liveAgents),
        formatDate(c.createdAt),
        c.relaySessionId,
      ]),
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="u-eyebrow">Control plane</p>
          <h1 className={styles.h1}>Channels</h1>
        </div>
        {!isPending && !isError && (
          <div className={styles.headActions}>
            {channels && channels.length > 0 && (
              <Button variant="secondary" size="sm" onClick={exportCsv}>
                Export CSV
              </Button>
            )}
            {/* At the limit the "New channel" action is withheld — the limit banner below
                carries the single, explicit upgrade CTA instead. */}
            {canCreate && (
              <Button onClick={openCreate} iconBefore={<PlusIcon />}>
                New channel
              </Button>
            )}
          </div>
        )}
      </header>

      {/* At the plan's channel limit — say so explicitly and route to an upgrade, rather
          than offering a create form that the API would reject on submit. */}
      {atChannelLimit && usage && (
        <Callout tone="warning" title="You've reached your channel limit">
          <p className={styles.limitBody}>
            Your <strong>{TIER_LABEL[usage.tier] ?? usage.tier}</strong> plan includes{" "}
            <strong>
              {usage.channels.limit} channel{usage.channels.limit === 1 ? "" : "s"}
            </strong>{" "}
            and you're using all {usage.channels.used}. To add another channel, upgrade your plan.
          </p>
          <div className={styles.limitCta}>
            <Button onClick={() => navigate("/app/billing")}>Upgrade plan</Button>
          </div>
        </Callout>
      )}

      {isPending && (
        <div className={styles.loading}>
          <Spinner size={22} color="var(--accent)" /> <span>Loading channels…</span>
        </div>
      )}

      {unavailable && (
        <Callout tone="warning" title="Channels aren't enabled yet">
          The control plane isn't linked to a Cogent relay, so channels can't be managed from here yet.
          Once the relay connection is configured, your channels will appear here.
        </Callout>
      )}

      {isError && !unavailable && (
        <Callout tone="danger" title="Couldn't load channels">
          {apiMessage(error, "Please try again shortly.")}
        </Callout>
      )}

      {channels && channels.length === 0 && (
        <Card className={styles.empty}>
          <h2 className={styles.emptyTitle}>No channels yet</h2>
          <p className={styles.emptyBlurb}>
            A channel maps a Cogent relay session your agents join to a Slack conversation. Create one,
            then bridge it with <code>/cogent map</code>.
          </p>
          {!atChannelLimit && (
            <Button onClick={openCreate} iconBefore={<PlusIcon />}>
              Create your first channel
            </Button>
          )}
        </Card>
      )}

      {channels && channels.length > 0 && (
        <ul className={styles.list}>
          {channels.map((ch) => (
            <li key={ch.id}>
              <Card className={styles.row}>
                <div className={styles.rowMain}>
                  <h2 className={styles.name}>{ch.name}</h2>
                  <div className={styles.status}>
                    <Badge tone={ch.slackChannels > 0 ? "success" : "neutral"}>
                      {ch.slackChannels > 0
                        ? `Mapped to ${ch.slackChannels} Slack ${ch.slackChannels === 1 ? "channel" : "channels"}`
                        : "Not mapped to Slack"}
                    </Badge>
                    {/* Live agents is relay-sourced — shown only when known (degrades to
                        absent rather than a fabricated/zero count). */}
                    {ch.liveAgents !== null && (
                      <span className={styles.live}>
                        {ch.liveAgents} {ch.liveAgents === 1 ? "agent" : "agents"} live
                      </span>
                    )}
                    {/* Retention: a durable channel reads as a "Permanent" badge; otherwise
                        the idle window before the relay sweeps it (default 1 month). */}
                    {ch.neverDelete ? (
                      <Badge tone="success">Permanent</Badge>
                    ) : (
                      <span className={styles.retention}>{retentionSummary(ch)}</span>
                    )}
                  </div>
                  <p className={styles.meta}>Created {formatDate(ch.createdAt)}</p>
                  {(() => {
                    const chAgents = agentsBySession.get(ch.relaySessionId) ?? [];
                    if (chAgents.length === 0) return null;
                    return (
                      <div className={styles.agents}>
                        <p className={styles.agentsHead}>
                          {chAgents.length} {chAgents.length === 1 ? "agent" : "agents"} connected
                        </p>
                        <AgentList agents={chAgents} />
                      </div>
                    );
                  })()}
                </div>
                <div className={styles.rowActions}>
                  <Button variant="secondary" size="sm" onClick={() => openRetention(ch)}>
                    Retention
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => openRotate(ch)}>
                    Rotate secret
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => openDelete(ch)}>
                    Disconnect
                  </Button>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {/* Create channel */}
      <Modal
        open={createOpen}
        onClose={closeCreate}
        title={created ? "Channel created" : "New channel"}
        footer={
          created ? (
            <Button onClick={closeCreate}>Done</Button>
          ) : undefined
        }
      >
        {created ? (
          <div className={styles.success}>
            <p>
              <strong>{created.channel.name}</strong> is ready. In a <strong>public</strong> Slack
              channel, just run this command — <code>/cogent map</code> adds the Cogent bot for you
              automatically (private channels: type <code>@Cogent</code> → <strong>Add to channel</strong>{" "}
              first). Replace <code>YOUR_PASSWORD</code> with the password you just chose and{" "}
              <code>YOUR_ORG_ID</code> with your organization's Org_ID:
            </p>
            <CopyField
              label="Slack command"
              value={`/cogent map ${created.channel.name} YOUR_PASSWORD YOUR_ORG_ID`}
              multiline
            />
            <Callout tone="info">
              Agents join with the channel name, the password you set, and your Org_ID. For your
              security Cogent never displays the password again — keep it safe. To change it later,
              use <strong>Rotate secret</strong> on the channel.
            </Callout>
          </div>
        ) : (
          <CreateChannelForm
            pending={create.isPending}
            error={create.isError ? apiMessage(create.error, "Couldn't create the channel.") : null}
            onSubmit={(values) => create.mutate(values)}
          />
        )}
      </Modal>

      {/* Rotate secret */}
      <Modal
        open={rotateOpen}
        onClose={closeRotate}
        title={rotated ? "Secret rotated" : "Rotate channel secret"}
        footer={rotated ? <Button onClick={closeRotate}>Done</Button> : undefined}
      >
        {rotated ? (
          <div className={styles.success}>
            <p>
              <strong>{rotated.channel.name}</strong>'s secret was rotated. The old secret stops working{" "}
              <strong>immediately</strong> — agents must rejoin with the new one. Run this in Slack,
              replacing <code>YOUR_PASSWORD</code> with the new password you just set and{" "}
              <code>YOUR_ORG_ID</code> with your Org_ID:
            </p>
            <CopyField
              label="Slack command"
              value={`/cogent map ${rotated.channel.name} YOUR_PASSWORD YOUR_ORG_ID`}
              multiline
            />
            <Callout tone="warning" title="The old secret no longer works">
              Update every agent that joins this channel with the new password. For your security
              Cogent never displays it again — keep it safe.
            </Callout>
          </div>
        ) : (
          <RotateSecretForm
            channelName={rotateTarget?.name ?? ""}
            pending={rotate.isPending}
            error={rotate.isError ? apiMessage(rotate.error, "Couldn't rotate the secret.") : null}
            onSubmit={(secret) => rotateTarget && rotate.mutate({ channel: rotateTarget, secret })}
          />
        )}
      </Modal>

      {/* Retention policy */}
      <Modal
        open={retentionTarget !== null}
        onClose={closeRetention}
        title={`Retention — ${retentionTarget?.name ?? ""}`}
      >
        {retentionTarget && (
          <RetentionForm
            channel={retentionTarget}
            pending={setRetention.isPending}
            error={setRetention.isError ? apiMessage(setRetention.error, "Couldn't update retention.") : null}
            onCancel={closeRetention}
            onSubmit={(input) => setRetention.mutate({ id: retentionTarget.id, input })}
          />
        )}
      </Modal>

      {/* Disconnect confirm */}
      <Modal
        open={toDelete !== null}
        onClose={closeDelete}
        title="Disconnect channel?"
        footer={
          <>
            <Button variant="ghost" onClick={closeDelete} disabled={remove.isPending}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => toDelete && remove.mutate(toDelete.id)}
            >
              Disconnect
            </Button>
          </>
        }
      >
        <p>
          Disconnect <strong>{toDelete?.name}</strong>? Its relay session is torn down and any Slack
          channels mapped to it stop relaying. Agents using it will no longer reach Slack. This can't be
          undone.
        </p>
        {remove.isError && (
          <Callout tone="danger">{apiMessage(remove.error, "Couldn't disconnect the channel.")}</Callout>
        )}
      </Modal>
    </div>
  );
}

// --- Create form (RHF + Zod, mirrors api createChannelSchema: name pattern +
//     secret min1). Lives inside the Modal, so it unmounts on close → fresh state
//     on each open. ---
// Canonical channel-name rule (matches the relay session-label pattern and the
// backend createChannelSchema): lowercase letters/digits/hyphens, 3–32 chars, must
// start and end with a letter or digit. Enforced here so spaces/capitals are rejected
// BEFORE submit with a clear message (COG-1).
const CHANNEL_NAME_RE = /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/;
const CHANNEL_NAME_MSG =
  "Use lowercase letters, numbers, and hyphens only — no spaces or capitals (3–32 characters, can't start or end with a hyphen).";
// Channel-secret rule (matches the backend). The secret is used in the space-delimited
// `/cogent map <name> <secret> <org-id>` command and the quoted `channel password "<secret>"`
// register form, so whitespace and quotes/backslash break parsing — reject only those and require
// a reasonable length. Other special characters are allowed on purpose (they strengthen it). (COG-2)
const SECRET_MIN = 8;
const channelSecret = (requiredMsg: string) =>
  z
    .string()
    .min(1, requiredMsg)
    .min(SECRET_MIN, `Channel secret must be at least ${SECRET_MIN} characters`)
    .refine((s) => !/\s/.test(s), "Channel secret can't contain spaces or whitespace")
    .refine((s) => !/["\\]/.test(s), 'Channel secret can\'t contain quotes (") or backslashes (\\)');
const createChannelSchema = z.object({
  name: z.string().trim().min(1, "Channel name is required").regex(CHANNEL_NAME_RE, CHANNEL_NAME_MSG),
  secret: channelSecret("A channel secret is required"),
});
type CreateChannelValues = z.infer<typeof createChannelSchema>;

function CreateChannelForm({
  pending,
  error,
  onSubmit,
}: {
  pending: boolean;
  error: string | null;
  onSubmit: (values: CreateChannelValues) => void;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateChannelValues>({
    resolver: zodResolver(createChannelSchema),
    defaultValues: { name: "", secret: "" },
  });

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      {error && (
        <div role="alert">
          <Callout tone="danger">{error}</Callout>
        </div>
      )}
      <TextField
        label="Channel name"
        placeholder="backend-team"
        hint="Lowercase letters, numbers, and hyphens only — no spaces or capitals. The name your agents and Slack will share."
        required
        mono
        error={errors.name?.message}
        {...register("name")}
      />
      <PasswordField
        label="Channel secret"
        autoComplete="off"
        placeholder="A shared password agents use to join"
        hint="At least 8 characters. No spaces, quotes, or backslashes (special characters like @ # ! are allowed)."
        required
        error={errors.secret?.message}
        {...register("secret")}
      />
      <Button type="submit" fullWidth loading={pending}>
        Create channel
      </Button>
    </form>
  );
}

// --- Rotate-secret form (RHF + Zod, mirrors api rotateSecretSchema: secret min1).
//     Lives inside the Modal, so it unmounts on close → fresh state on each open. ---
const rotateSecretSchema = z.object({
  secret: channelSecret("A new channel secret is required"),
});
type RotateSecretValues = z.infer<typeof rotateSecretSchema>;

function RotateSecretForm({
  channelName,
  pending,
  error,
  onSubmit,
}: {
  channelName: string;
  pending: boolean;
  error: string | null;
  onSubmit: (secret: string) => void;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RotateSecretValues>({
    resolver: zodResolver(rotateSecretSchema),
    defaultValues: { secret: "" },
  });

  return (
    <form className={styles.form} onSubmit={handleSubmit((v) => onSubmit(v.secret))} noValidate>
      <p className={styles.formIntro}>
        Set a new secret for <strong>{channelName}</strong>. The current secret stops working
        immediately and connected agents must rejoin with the new one.
      </p>
      {error && (
        <div role="alert">
          <Callout tone="danger">{error}</Callout>
        </div>
      )}
      <PasswordField
        label="New channel secret"
        autoComplete="off"
        placeholder="A new shared password agents use to join"
        required
        error={errors.secret?.message}
        {...register("secret")}
      />
      <Button type="submit" variant="danger" fullWidth loading={pending}>
        Rotate secret
      </Button>
    </form>
  );
}

// --- Retention form (plain controlled inputs — a checkbox + a preset select, no
//     RHF needed for two fields). Lives inside the Modal, so it unmounts on close →
//     re-initializes from the channel on each open. ---
function RetentionForm({
  channel,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  channel: Channel;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (input: RetentionInput) => void;
}) {
  const [neverDelete, setNeverDelete] = useState(channel.neverDelete);
  const [preset, setPreset] = useState(() => timeoutToPreset(channel.retentionTimeoutMs));

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // Always send neverDelete explicitly. Send an idle-timeout override only when the
    // channel is NOT permanent and a non-default window is chosen (default = omit → inherit).
    const input: RetentionInput = { neverDelete };
    if (!neverDelete) {
      const timeoutMs = presetToTimeout(preset);
      if (timeoutMs !== undefined) input.timeoutMs = timeoutMs;
    }
    onSubmit(input);
  }

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <p className={styles.formIntro}>
        Control when an idle channel is removed. By default a channel is deleted after{" "}
        <strong>1 month</strong> with no activity. Make it <strong>permanent</strong> to keep it
        forever, or pick a different idle window.{" "}
        <a href="https://cogent.tools/faq#retention" target="_blank" rel="noreferrer">
          Learn about retention
        </a>
        .
      </p>
      {error && (
        <div role="alert">
          <Callout tone="danger">{error}</Callout>
        </div>
      )}
      <Checkbox
        label="Never delete this channel (keep it permanently)"
        checked={neverDelete}
        onChange={(e) => setNeverDelete(e.target.checked)}
      />
      <Select
        label="Idle timeout"
        options={RETENTION_OPTIONS}
        value={preset}
        disabled={neverDelete}
        hint={
          neverDelete
            ? "Never-delete is on — this channel is kept regardless of idle time."
            : "How long the channel can sit idle before the relay removes it."
        }
        onChange={(e) => setPreset(e.target.value)}
      />
      <div className={styles.formActions}>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" loading={pending}>
          Save retention
        </Button>
      </div>
    </form>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}
